import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import useAccounting from '../hooks/useAccounting';
import { formaterEuro } from '../services/tarificationService';
import { STATUTS } from '../services/accountingService';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Tableau de bord comptable principal - Version Responsive
 */

// Widget de statistique responsive
const WidgetStatistique = ({ titre, valeur, sousTitre, couleur = 'blue', icone }) => (
  <div className={`bg-white dark:bg-gray-800 p-4 md:p-6 rounded-xl shadow-sm border-l-4 border-${couleur}-500`}>
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <p className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{titre}</p>
        <p className="text-lg md:text-2xl font-bold text-gray-900 dark:text-white mt-1">{valeur}</p>
        {sousTitre && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sousTitre}</p>}
      </div>
      {icone && (
        <div className={`text-${couleur}-500 dark:text-${couleur}-400 text-xl md:text-2xl ml-2`}>
          {icone}
        </div>
      )}
    </div>
  </div>
);

// Composant pour afficher les éléments en mode carte (mobile)
const CarteElement = ({ element, type, getLibelleStatut }) => (
  <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-3">
    <div className="flex justify-between items-start mb-2">
      <div className="flex-1">
        <p className="font-medium text-sm text-gray-900 dark:text-white">{element.clientNom}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {type === 'devis' ? 
            `Créé le ${new Date(element.dateCreation).toLocaleDateString('fr-FR')}` :
            `Émis le ${new Date(element.dateEmission).toLocaleDateString('fr-FR')}`
          }
        </p>
      </div>
      <span className={`px-2 py-1 rounded-full text-xs font-medium min-w-[80px] text-center ${
        type === 'devis' ? 
          (element.statut === STATUTS.DEVIS.ACCEPTE ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' :
           element.statut === STATUTS.DEVIS.ENVOYE ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300' :
           'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300') :
        (element.statut === STATUTS.FACTURE.PAYEE ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' :
         element.estEnRetard ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300' :
         'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300')
      }`}>
        {type === 'devis' ? 
          getLibelleStatut(element.statut, 'devis') :
          getLibelleStatut(element.statut, 'facture')
        }
      </span>
    </div>
    <div className="flex justify-between items-center">
      <p className="text-sm font-semibold text-gray-900 dark:text-white">
        {formaterEuro(element.montantTTC)}
      </p>
      {type === 'facture' && element.estEnRetard && (
        <span className="text-xs text-red-600 dark:text-red-400 font-medium">En retard</span>
      )}
    </div>
  </div>
);

const AccountingDashboard = () => {
  const {
    devis,
    factures,
    statistiques,
    loading,
    error,
    rechargerDonnees,
    devisEnCours,
    devisEnvoyes,
    devisAcceptes,
    facturesEnAttente,
    facturesPayees,
    facturesEnRetard,
    totalEnRetard,
    getChiffreAffairesMensuel
  } = useAccounting(db);

  const [periodeStatistiques, setPeriodeStatistiques] = useState({
    debut: new Date(new Date().getFullYear(), 0, 1),
    fin: new Date()
  });
  const [ongletActif, setOngletActif] = useState('tableau-de-bord');
  const { theme } = useTheme();

  // États des filtres pour les factures mandat
  const [filtresFactures, setFiltresFactures] = useState({
    recherche: '',
    periode: 'tous',
    montantMin: '',
    montantMax: '',
    clientId: 'tous',
    statut: 'tous',
    dateEcheance: 'tous'
  });

  // Formater les dates pour l'affichage
  const formaterDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  // Obtenir le libellé du statut
  const getLibelleStatut = (statut, type) => {
    const statuts = STATUTS[type.toUpperCase()];
    for (const [key, value] of Object.entries(statuts)) {
      if (value === statut) {
        return key.toLowerCase().replace('_', ' ');
      }
    }
    return statut;
  };

  // Filtrer les factures mandat
  const facturesFiltrees = factures.filter(facture => {
    // Filtre recherche (N° facture ou nom client)
    const matchRecherche = !filtresFactures.recherche ||
      facture.numero?.toLowerCase().includes(filtresFactures.recherche.toLowerCase()) ||
      facture.clientNom?.toLowerCase().includes(filtresFactures.recherche.toLowerCase());

    // Filtre période
    let matchPeriode = true;
    if (filtresFactures.periode !== 'tous' && facture.dateEmission) {
      const now = new Date();
      const dateEmission = facture.dateEmission;

      switch (filtresFactures.periode) {
        case 'semaine':
          const debutSemaine = new Date(now);
          debutSemaine.setDate(now.getDate() - now.getDay());
          matchPeriode = dateEmission >= debutSemaine;
          break;
        case 'mois':
          matchPeriode = dateEmission.getMonth() === now.getMonth() &&
                        dateEmission.getFullYear() === now.getFullYear();
          break;
        case 'trimestre':
          const trimestreActuel = Math.floor(now.getMonth() / 3);
          const trimestreFacture = Math.floor(dateEmission.getMonth() / 3);
          matchPeriode = trimestreFacture === trimestreActuel &&
                        dateEmission.getFullYear() === now.getFullYear();
          break;
        case 'annee':
          matchPeriode = dateEmission.getFullYear() === now.getFullYear();
          break;
        default:
          matchPeriode = true;
      }
    }

    // Filtre montant
    const matchMontantMin = !filtresFactures.montantMin ||
      facture.montantTTC >= parseFloat(filtresFactures.montantMin);
    const matchMontantMax = !filtresFactures.montantMax ||
      facture.montantTTC <= parseFloat(filtresFactures.montantMax);

    // Filtre client
    const matchClient = filtresFactures.clientId === 'tous' ||
      facture.clientId === filtresFactures.clientId;

    // Filtre statut
    const matchStatut = filtresFactures.statut === 'tous' ||
      facture.statut === filtresFactures.statut;

    // Filtre date d'échéance
    let matchDateEcheance = true;
    if (filtresFactures.dateEcheance !== 'tous' && facture.dateEcheance) {
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const echeance = new Date(facture.dateEcheance);
      echeance.setHours(0, 0, 0, 0);

      switch (filtresFactures.dateEcheance) {
        case 'avenir':
          matchDateEcheance = echeance > now;
          break;
        case 'aujourdhui':
          matchDateEcheance = echeance.getTime() === now.getTime();
          break;
        case 'depassee':
          matchDateEcheance = echeance < now;
          break;
        default:
          matchDateEcheance = true;
      }
    }

    return matchRecherche && matchPeriode && matchMontantMin &&
           matchMontantMax && matchClient && matchStatut && matchDateEcheance;
  });

  // Statistiques filtrées
  const facturesFiltreesEnAttente = facturesFiltrees.filter(f => f.statut === 'en_attente');
  const facturesFiltreesPayees = facturesFiltrees.filter(f => f.statut === 'payee');
  const facturesFiltreesEnRetard = facturesFiltrees.filter(f => f.estEnRetard);
  const totalMontantFiltre = facturesFiltrees.reduce((sum, f) => sum + f.montantTTC, 0);

  // Réinitialiser les filtres
  const reinitialiserFiltres = () => {
    setFiltresFactures({
      recherche: '',
      periode: 'tous',
      montantMin: '',
      montantMax: '',
      clientId: 'tous',
      statut: 'tous',
      dateEcheance: 'tous'
    });
  };

  // Appliquer un filtre rapide
  const appliquerFiltreRapide = (type) => {
    reinitialiserFiltres();
    switch (type) {
      case 'en_attente':
        setFiltresFactures(prev => ({ ...prev, statut: 'en_attente' }));
        break;
      case 'payee':
        setFiltresFactures(prev => ({ ...prev, statut: 'payee' }));
        break;
      case 'retard':
        setFiltresFactures(prev => ({ ...prev, dateEcheance: 'depassee', statut: 'en_attente' }));
        break;
      case 'mois':
        setFiltresFactures(prev => ({ ...prev, periode: 'mois' }));
        break;
      default:
        reinitialiserFiltres();
    }
  };

  // Afficher le chargement
  if (loading && !devis.length && !factures.length) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Chargement des données comptables...</p>
          </div>
        </div>
      </div>
    );
  }

  // Afficher l'erreur
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 max-w-4xl mx-auto">
          <div className="flex items-center">
            <div className="text-red-500 dark:text-red-400 text-2xl mr-3">⚠️</div>
            <div>
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-300">Erreur de chargement</h3>
              <p className="text-red-600 dark:text-red-400 mt-1">{error}</p>
              <button 
                onClick={rechargerDonnees}
                className="mt-3 px-4 py-2 bg-red-600 dark:bg-red-700 text-white rounded-lg hover:bg-red-700 dark:hover:bg-red-800 text-sm"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* En-tête responsive */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center py-4 md:py-6 space-y-4 md:space-y-0">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">Tableau de Bord Comptable</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base mt-1">Gestion des devis, factures et finances</p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={rechargerDonnees}
                className="px-3 py-2 md:px-4 md:py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 text-xs md:text-sm min-h-[44px]"
              >
                Actualiser
              </button>
              <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                {formaterDate(new Date())}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation par onglets responsive */}
      <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex overflow-x-auto md:overflow-visible space-x-2 md:space-x-8 py-2 md:py-0">
            {[
              { id: 'tableau-de-bord', label: 'Tableau de bord', icone: '📊' },
              { id: 'devis', label: 'Devis', icone: '📄' },
              { id: 'factures', label: 'Factures Mandat', icone: '💰' },
              { id: 'statistiques', label: 'Statistiques', icone: '📈' }
            ].map((onglet) => (
              <button
                key={onglet.id}
                onClick={() => setOngletActif(onglet.id)}
                className={`flex-shrink-0 py-3 md:py-4 px-2 md:px-1 border-b-2 font-medium text-xs md:text-sm min-w-[100px] md:min-w-0 ${
                  ongletActif === onglet.id
                    ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="mr-1 md:mr-2">{onglet.icone}</span>
                {onglet.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Contenu principal responsive */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 md:py-8">
        
        {/* Tableau de bord */}
        {ongletActif === 'tableau-de-bord' && (
          <div className="space-y-6">
            {/* Widgets principaux - Facturation Mandat */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <WidgetStatistique
                titre="Chiffre d'affaires mensuel"
                valeur={formaterEuro(getChiffreAffairesMensuel())}
                sousTitre="Facturation mandat"
                couleur="green"
                icone="💰"
              />
              <WidgetStatistique
                titre="Factures envoyées"
                valeur={facturesEnAttente.length}
                sousTitre="En attente de paiement"
                couleur="orange"
                icone="📄"
              />
              <WidgetStatistique
                titre="Factures payées"
                valeur={facturesPayees.length}
                sousTitre={formaterEuro(facturesPayees.reduce((sum, f) => sum + f.montantTTC, 0))}
                couleur="green"
                icone="✅"
              />
              <WidgetStatistique
                titre="Factures en retard"
                valeur={facturesEnRetard.length}
                sousTitre={formaterEuro(totalEnRetard)}
                couleur="red"
                icone="⚠️"
              />
            </div>

            {/* Alertes importantes - Facturation Mandat */}
            {(facturesEnRetard.length > 0 || facturesEnAttente.length > 5) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {facturesEnRetard.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                    <div className="flex items-center">
                      <div className="text-red-500 dark:text-red-400 text-xl mr-3">⚠️</div>
                      <div>
                        <h4 className="font-semibold text-red-800 dark:text-red-300">Factures en retard</h4>
                        <p className="text-red-600 dark:text-red-400 text-sm mt-1">
                          {facturesEnRetard.length} facture(s) mandat en retard pour un total de {formaterEuro(totalEnRetard)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {facturesEnAttente.length > 5 && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
                    <div className="flex items-center">
                      <div className="text-orange-500 dark:text-orange-400 text-xl mr-3">📄</div>
                      <div>
                        <h4 className="font-semibold text-orange-800 dark:text-orange-300">Factures en attente</h4>
                        <p className="text-orange-600 dark:text-orange-400 text-sm mt-1">
                          {facturesEnAttente.length} factures mandat envoyées en attente de paiement
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Derniers devis et factures */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Derniers devis */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-4">Derniers devis</h3>
                {/* Version mobile - Cartes */}
                <div className="md:hidden space-y-3">
                  {devis.slice(0, 5).map((devis) => (
                    <CarteElement key={devis.id} element={devis} type="devis" getLibelleStatut={getLibelleStatut} />
                  ))}
                  {devis.length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">Aucun devis trouvé</p>
                  )}
                </div>
                {/* Version desktop - Tableau */}
                <div className="hidden md:block space-y-3">
                  {devis.slice(0, 5).map((devis) => (
                    <div key={devis.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-white">{devis.clientNom}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formaterDate(devis.dateCreation)} • {formaterEuro(devis.montantTTC)}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        devis.statut === STATUTS.DEVIS.ACCEPTE ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' :
                        devis.statut === STATUTS.DEVIS.ENVOYE ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300' :
                        'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300'
                      }`}>
                        {getLibelleStatut(devis.statut, 'devis')}
                      </span>
                    </div>
                  ))}
                  {devis.length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">Aucun devis trouvé</p>
                  )}
                </div>
              </div>

              {/* Dernières factures mandat */}
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 md:p-6">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-white mb-4">Dernières factures mandat</h3>
                {/* Version mobile - Cartes */}
                <div className="md:hidden space-y-3">
                  {factures.slice(0, 5).map((facture) => (
                    <CarteElement key={facture.id} element={facture} type="facture" getLibelleStatut={getLibelleStatut} />
                  ))}
                  {factures.length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">Aucune facture trouvée</p>
                  )}
                </div>
                {/* Version desktop - Tableau */}
                <div className="hidden md:block space-y-3">
                  {factures.slice(0, 5).map((facture) => (
                    <div key={facture.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                      <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-white">{facture.clientNom}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {formaterDate(facture.dateEmission)} • {formaterEuro(facture.montantTTC)}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        facture.statut === STATUTS.FACTURE.PAYEE ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' :
                        facture.estEnRetard ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300' :
                        'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300'
                      }`}>
                        {getLibelleStatut(facture.statut, 'facture')}
                      </span>
                    </div>
                  ))}
                  {factures.length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">Aucune facture trouvée</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Onglet Devis */}
        {ongletActif === 'devis' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <div className="p-4 md:p-6 border-b dark:border-gray-700">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Gestion des devis</h2>
            </div>
            <div className="p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400">{devisEnCours.length}</div>
                  <div className="text-xs md:text-sm text-blue-800 dark:text-blue-300">En cours</div>
                </div>
                <div className="text-center p-3 md:p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-orange-600 dark:text-orange-400">{devisEnvoyes.length}</div>
                  <div className="text-xs md:text-sm text-orange-800 dark:text-orange-300">Envoyés</div>
                </div>
                <div className="text-center p-3 md:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-green-600 dark:text-green-400">{devisAcceptes.length}</div>
                  <div className="text-xs md:text-sm text-green-800 dark:text-green-300">Acceptés</div>
                </div>
              </div>

              {/* Version mobile - Cartes */}
              <div className="md:hidden space-y-4">
                {devis.map((devis) => (
                  <CarteElement key={devis.id} element={devis} type="devis" getLibelleStatut={getLibelleStatut} />
                ))}
                {devis.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucun devis trouvé
                  </div>
                )}
              </div>

              {/* Version desktop - Tableau */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm bg-white dark:bg-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">N° Devis</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bénéficiaire</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Montant</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {devis.map((devis) => (
                      <tr key={devis.id} className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{devis.numero}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{devis.clientNom}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">{formaterEuro(devis.montantTTC)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300">{formaterDate(devis.dateCreation)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            devis.statut === STATUTS.DEVIS.ACCEPTE ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' :
                            devis.statut === STATUTS.DEVIS.ENVOYE ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300' :
                            devis.statut === STATUTS.DEVIS.BROUILLON ? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300' :
                            'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                          }`}>
                            {getLibelleStatut(devis.statut, 'devis')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {devis.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucun devis trouvé
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Onglet Factures */}
        {ongletActif === 'factures' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <div className="p-4 md:p-6 border-b dark:border-gray-700">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Gestion des factures</h2>
            </div>
            <div className="p-4 md:p-6">
              {/* Statistiques filtrées */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400">{facturesFiltreesEnAttente.length}</div>
                  <div className="text-xs md:text-sm text-blue-800 dark:text-blue-300">En attente</div>
                </div>
                <div className="text-center p-3 md:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-green-600 dark:text-green-400">{facturesFiltreesPayees.length}</div>
                  <div className="text-xs md:text-sm text-green-800 dark:text-green-300">Payées</div>
                </div>
                <div className="text-center p-3 md:p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-red-600 dark:text-red-400">{facturesFiltreesEnRetard.length}</div>
                  <div className="text-xs md:text-sm text-red-800 dark:text-red-300">En retard</div>
                </div>
                <div className="text-center p-3 md:p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <div className="text-xl md:text-2xl font-bold text-purple-600 dark:text-purple-400">{formaterEuro(totalMontantFiltre)}</div>
                  <div className="text-xs md:text-sm text-purple-800 dark:text-purple-300">Montant total</div>
                </div>
              </div>

              {/* Filtres rapides */}
              <div className="flex flex-wrap gap-2 mb-4">
                <button
                  onClick={() => appliquerFiltreRapide('tous')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filtresFactures.statut === 'tous' && filtresFactures.periode === 'tous'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Toutes
                </button>
                <button
                  onClick={() => appliquerFiltreRapide('en_attente')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filtresFactures.statut === 'en_attente'
                      ? 'bg-yellow-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  En attente ({facturesEnAttente.length})
                </button>
                <button
                  onClick={() => appliquerFiltreRapide('payee')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filtresFactures.statut === 'payee'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Payées ({facturesPayees.length})
                </button>
                <button
                  onClick={() => appliquerFiltreRapide('retard')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filtresFactures.dateEcheance === 'depassee'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  En retard ({facturesEnRetard.length})
                </button>
                <button
                  onClick={() => appliquerFiltreRapide('mois')}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filtresFactures.periode === 'mois'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Ce mois
                </button>
              </div>

              {/* Barre de filtres détaillés */}
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                  {/* Recherche */}
                  <div className="lg:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      🔍 Rechercher
                    </label>
                    <input
                      type="text"
                      value={filtresFactures.recherche}
                      onChange={(e) => setFiltresFactures(prev => ({ ...prev, recherche: e.target.value }))}
                      placeholder="N° facture ou client..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Période */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      📅 Période
                    </label>
                    <select
                      value={filtresFactures.periode}
                      onChange={(e) => setFiltresFactures(prev => ({ ...prev, periode: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="tous">Toutes</option>
                      <option value="semaine">Cette semaine</option>
                      <option value="mois">Ce mois</option>
                      <option value="trimestre">Ce trimestre</option>
                      <option value="annee">Cette année</option>
                    </select>
                  </div>

                  {/* Statut */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      📊 Statut
                    </label>
                    <select
                      value={filtresFactures.statut}
                      onChange={(e) => setFiltresFactures(prev => ({ ...prev, statut: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="tous">Tous</option>
                      <option value="brouillon">Générées</option>
                      <option value="en_attente">Envoyées</option>
                      <option value="payee">Payées</option>
                      <option value="annulee">Annulées</option>
                    </select>
                  </div>

                  {/* Échéance */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      📆 Échéance
                    </label>
                    <select
                      value={filtresFactures.dateEcheance}
                      onChange={(e) => setFiltresFactures(prev => ({ ...prev, dateEcheance: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="tous">Toutes</option>
                      <option value="avenir">À venir</option>
                      <option value="aujourdhui">Aujourd'hui</option>
                      <option value="depassee">Dépassée</option>
                    </select>
                  </div>
                </div>

                {/* Bouton réinitialiser */}
                <div className="mt-4 flex justify-between items-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {facturesFiltrees.length} facture(s) trouvée(s)
                  </div>
                  <button
                    onClick={reinitialiserFiltres}
                    className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    ↺ Réinitialiser
                  </button>
                </div>
              </div>

              {/* Version mobile - Cartes */}
              <div className="md:hidden space-y-4">
                {facturesFiltrees.map((facture) => (
                  <CarteElement key={facture.id} element={facture} type="facture" getLibelleStatut={getLibelleStatut} />
                ))}
                {facturesFiltrees.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucune facture trouvée
                  </div>
                )}
              </div>

              {/* Version desktop - Tableau */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm bg-white dark:bg-gray-800">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">N° Facture</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bénéficiaire</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Montant</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Émission</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Échéance</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Statut</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {facturesFiltrees.map((facture) => (
                      <tr key={facture.id} className="hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{facture.numero}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">{facture.clientNom}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-gray-100">{formaterEuro(facture.montantTTC)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-300">{formaterDate(facture.dateEmission)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`text-sm font-medium ${
                            facture.estEnRetard ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'
                          }`}>
                            {formaterDate(facture.dateEcheance)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            facture.statut === STATUTS.FACTURE.PAYEE ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300' :
                            facture.statut === STATUTS.FACTURE.EN_RETARD ? 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300' :
                            'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                          }`}>
                            {getLibelleStatut(facture.statut, 'facture')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {factures.length === 0 && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Aucune facture trouvée
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Onglet Statistiques */}
        {ongletActif === 'statistiques' && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm">
            <div className="p-6 border-b dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Statistiques financières</h2>
            </div>
            <div className="p-6">
              {statistiques ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <WidgetStatistique
                    titre="Chiffre d'affaires"
                    valeur={formaterEuro(statistiques.chiffreAffaires)}
                    sousTitre={`${statistiques.totalFactures} factures`}
                    couleur="green"
                    icone="💰"
                  />
                  <WidgetStatistique
                    titre="Factures payées"
                    valeur={statistiques.facturesPayees}
                    sousTitre={`${Math.round((statistiques.facturesPayees / statistiques.totalFactures) * 100)}%`}
                    couleur="blue"
                    icone="✅"
                  />
                  <WidgetStatistique
                    titre="En retard"
                    valeur={formaterEuro(statistiques.totalEnRetard)}
                    sousTitre={`${statistiques.facturesEnRetard} factures`}
                    couleur="red"
                    icone="⚠️"
                  />
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Aucune statistique disponible
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountingDashboard;
